import * as React from "react";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Avatar,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDataProvider, useLocaleState, useNotify, useTranslate } from "react-admin";
import { Service } from "../../../types";
import { reorderServices } from "./reorderServices";

const getCoverSrc = (cover: Service["cover"]): string | undefined => {
  if (!cover || typeof cover !== "string") return undefined;
  return `${window["location"]["protocol"]}//${window["env"]["apiDomain"]}/uploads/${cover}`;
};

const ServicesReorder = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const [locale] = useLocaleState();

  const [services, setServices] = useState<Service[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dataProvider
      .getList<Service>("services", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "order", order: "ASC" },
        filter: {},
      })
      .then(({ data }) => setServices(data))
      .catch(() => {
        notify("servicesReorder.notifications.loadError", { type: "error" });
        setServices([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !services) return;

    const reordered = Array.from(services);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    const previous = services;
    setServices(reordered);
    setSaving(true);

    try {
      await reorderServices(reordered.map((service) => service.id));
      notify("servicesReorder.notifications.success", { type: "success" });
    } catch (error) {
      setServices(previous);
      notify("servicesReorder.notifications.error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", mt: 2, mb: 6 }}>
      <Typography variant="h5" gutterBottom>
        {translate("servicesReorder.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate("servicesReorder.subtitle")}
      </Typography>

      {!services ? (
        <CircularProgress />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="services">
            {(provided) => (
              <Paper
                variant="outlined"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <List disablePadding>
                  {services.map((service, index) => (
                    <Draggable key={service.id} draggableId={service.id} index={index}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          divider={index < services.length - 1}
                          sx={{
                            bgcolor: snapshot.isDragging ? "action.hover" : undefined,
                            cursor: "grab",
                          }}
                        >
                          <DragIndicatorIcon sx={{ mr: 1, color: "text.disabled" }} />
                          <ListItemAvatar>
                            <Avatar
                              variant="rounded"
                              src={getCoverSrc(service.cover)}
                              alt=""
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={service.serviceName?.[locale] || service.id}
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              </Paper>
            )}
          </Droppable>
        </DragDropContext>
      )}
      {saving && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {translate("servicesReorder.saving")}
        </Typography>
      )}
    </Box>
  );
};

export default ServicesReorder;
